import React, { useEffect, useState, useCallback } from 'react';
import useUIStore from '../../store/uiStore';
import * as cashflowService from '../../services/cashflowService';
import * as projectService from '../../services/projectService';
import CashFlowTabs from '../../components/CashFlowTabs';
import ScrollX from '../../components/ScrollX';

const money = (n) => `₹${Number(n || 0).toLocaleString('en-IN')}`;

const STATUS_COLORS = {
  planned: 'bg-gray-200 text-gray-800',
  requested: 'bg-amber-100 text-amber-800',
  approved: 'bg-blue-100 text-blue-800',
  paid: 'bg-green-100 text-green-800',
  cancelled: 'bg-red-100 text-red-800',
};
const STATUS_OPTIONS = ['planned', 'requested', 'approved', 'paid', 'cancelled'];

const initialForm = { project_id: '', purpose: '', amount: '', needed_by_date: '', notes: '' };

const SiteRequirementsPage = () => {
  const showToast = useUIStore((s) => s.showToast);
  const [projects, setProjects] = useState([]);
  const [rows, setRows] = useState([]);
  const [filters, setFilters] = useState({ project_id: '', status: '' });
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(initialForm);
  const [isLoading, setIsLoading] = useState(false);

  const projectName = (id) => projects.find((p) => p.id === id)?.name || `#${id}`;

  const loadAll = useCallback(async () => {
    setIsLoading(true);
    try {
      const params = {};
      if (filters.project_id) params.project_id = filters.project_id;
      if (filters.status) params.status = filters.status;
      const [projRes, siteRes] = await Promise.all([
        projects.length ? Promise.resolve({ data: { projects } }) : projectService.getProjects(),
        cashflowService.getSiteRequirements(params),
      ]);
      if (!projects.length) setProjects(projRes.data.projects || []);
      setRows(siteRes.data.site_requirements || []);
    } catch (error) {
      showToast(error.response?.data?.detail || 'Failed to load site requirements', 'error');
    } finally {
      setIsLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  useEffect(() => { loadAll(); }, [loadAll]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.project_id || !form.purpose || !form.amount || !form.needed_by_date) {
      showToast('Project, purpose, amount, and needed-by date are required', 'error');
      return;
    }
    try {
      await cashflowService.createSiteRequirement({
        project_id: Number(form.project_id), purpose: form.purpose, amount: Number(form.amount),
        needed_by_date: form.needed_by_date, notes: form.notes || null,
      });
      showToast('Site requirement added', 'success');
      setForm(initialForm);
      setShowForm(false);
      loadAll();
    } catch (error) {
      showToast(error.response?.data?.detail || 'Failed to add requirement', 'error');
    }
  };

  const handleStatusChange = async (row, status) => {
    try {
      await cashflowService.updateSiteRequirement(row.id, { status });
      showToast('Status updated', 'success');
      loadAll();
    } catch (error) {
      showToast(error.response?.data?.detail || 'Failed to update status', 'error');
    }
  };

  const handleDelete = async (row) => {
    if (!window.confirm('Delete this site requirement?')) return;
    try {
      await cashflowService.deleteSiteRequirement(row.id);
      showToast('Deleted', 'success');
      loadAll();
    } catch (error) {
      showToast(error.response?.data?.detail || 'Failed to delete', 'error');
    }
  };

  return (
    <div className="min-h-screen p-6">
      <div className="max-w-6xl mx-auto">
        <CashFlowTabs />
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-2xl font-bold text-white">Site Payment Requirements</h1>
          <button onClick={() => setShowForm((v) => !v)} className="text-sm px-4 py-2 rounded bg-white text-komal-blue font-semibold hover:bg-white/90">
            {showForm ? 'Cancel' : '+ Add Requirement'}
          </button>
        </div>

        {showForm && (
          <form onSubmit={handleSubmit} className="glass-card p-5 mb-6 grid md:grid-cols-3 gap-3">
            <select value={form.project_id} onChange={(e) => setForm({ ...form, project_id: e.target.value })} className="border border-gray-300 rounded px-2 py-1.5 text-sm">
              <option value="">Select project *</option>
              {projects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
            <input placeholder="Purpose (e.g. Site labour - week 1) *" value={form.purpose} onChange={(e) => setForm({ ...form, purpose: e.target.value })} className="border border-gray-300 rounded px-2 py-1.5 text-sm" />
            <input type="date" value={form.needed_by_date} onChange={(e) => setForm({ ...form, needed_by_date: e.target.value })} className="border border-gray-300 rounded px-2 py-1.5 text-sm" />
            <input type="number" placeholder="Amount (₹) *" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} className="border border-gray-300 rounded px-2 py-1.5 text-sm" />
            <input placeholder="Notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="border border-gray-300 rounded px-2 py-1.5 text-sm md:col-span-2" />
            <button type="submit" className="text-sm px-4 py-1.5 rounded bg-komal-blue text-white font-semibold">Save</button>
          </form>
        )}

        <div className="glass-card p-4 mb-6 flex flex-wrap gap-2 items-center">
          <select value={filters.project_id} onChange={(e) => setFilters({ ...filters, project_id: e.target.value })} className="border border-gray-300 rounded px-2 py-1.5 text-xs">
            <option value="">All projects</option>
            {projects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
          <select value={filters.status} onChange={(e) => setFilters({ ...filters, status: e.target.value })} className="border border-gray-300 rounded px-2 py-1.5 text-xs">
            <option value="">All statuses</option>
            {STATUS_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>

        <div className="glass-card">
          <div className="px-5 py-4 border-b border-gray-200">
            <h2 className="font-semibold text-gray-900">Requirements ({rows.length})</h2>
          </div>
          {isLoading ? (
            <p className="text-sm text-gray-400 p-5">Loading...</p>
          ) : rows.length === 0 ? (
            <p className="text-gray-500 text-sm p-5">No site payment requirements recorded yet.</p>
          ) : (
            <ScrollX>
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-gray-500 border-b border-gray-100">
                    <th className="px-4 py-2 font-medium">Project</th>
                    <th className="px-4 py-2 font-medium">Purpose</th>
                    <th className="px-4 py-2 font-medium">Needed by</th>
                    <th className="px-4 py-2 font-medium">Status</th>
                    <th className="px-4 py-2 font-medium">Amount</th>
                    <th className="px-4 py-2 font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.id} className="border-b border-gray-50 last:border-0">
                      <td className="px-4 py-3">{projectName(r.project_id)}</td>
                      <td className="px-4 py-3">{r.purpose}</td>
                      <td className="px-4 py-3">{r.needed_by_date}</td>
                      <td className="px-4 py-3">
                        <select
                          value={r.status}
                          onChange={(e) => handleStatusChange(r, e.target.value)}
                          className={`text-xs px-2 py-1 rounded font-semibold border-0 ${STATUS_COLORS[r.status]}`}
                        >
                          {STATUS_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
                        </select>
                      </td>
                      <td className="px-4 py-3 font-medium">{money(r.amount)}</td>
                      <td className="px-4 py-3">
                        {r.status !== 'paid' && (
                          <button onClick={() => handleDelete(r)} className="text-red-500 hover:underline text-xs font-semibold">Delete</button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </ScrollX>
          )}
        </div>
      </div>
    </div>
  );
};

export default SiteRequirementsPage;
